import { useMemo } from 'react';

import {
  TagIcon,
  BoxIcon,
  LanguagesIcon,
  ThumbsUpIcon,
  UserRoundIcon,
  LightbulbIcon,
  ToggleLeftIcon,
  BookmarkIcon,
  TypeIcon,
  MailIcon,
  PhoneIcon,
  MessageSquareIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getIntegrationByType } from '@/components/organisms/Integrations';
import { useAllInsights } from '@/components/pages/Insights/useInsights';
import {
  SearchableMenuItem,
  SearchableMenuGroup,
} from '@/components/ui/custom/SearchableDropdownMenu/SearchableDropdownMenu';
import { selectAvailableChannels } from '@/redux/integrations/selectors';
import { FilterViewListItem } from '@/shared/types/analytics';
import { INSIGHT_TYPES } from '@/shared/types/insights';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { FilterIconComponent as FilterIcon, FilterIconSpacer } from '@/util/filterFormatters';

/**
 * Custom hook that fetches and transforms all data needed for the filters dropdown
 * @param brainId - The brain ID to fetch data for
 * @param onFilterClick - Callback when a filter menu item is clicked
 * @param savedViews - Optional array of saved filter views
 * @param onLoadView - Optional callback when a saved view is selected
 * @returns Object containing transformed filter data
 */
export const useFiltersData = (
  brainId: string,
  onFilterClick: (filterId: string) => void,
  savedViews?: FilterViewListItem[],
  onLoadView?: (viewId: string) => void
) => {
  const { t } = useTranslation();
  const availableChannels = useSelector(selectAvailableChannels);
  const { data: allInsights } = useAllInsights(brainId);

  // Create insight submenu items based on insight data
  const insightMenuItems = useMemo((): SearchableMenuItem[] => {
    if (!allInsights || allInsights.length === 0) {
      return [];
    }

    return allInsights.map((insight) => {
      // Determine icon Component based on insight type
      let IconComponent = null;
      switch (insight.type) {
        case INSIGHT_TYPES.BOOLEAN:
          IconComponent = ToggleLeftIcon;
          break;
        case INSIGHT_TYPES.DATETIME:
          IconComponent = BoxIcon;
          break;
        case INSIGHT_TYPES.CATEGORICAL:
          IconComponent = TagIcon;
          break;
        case INSIGHT_TYPES.INTEGER:
        case INSIGHT_TYPES.NUMBER:
          IconComponent = LanguagesIcon;
          break;
        case INSIGHT_TYPES.STRING:
          IconComponent = TypeIcon;
          break;
        case INSIGHT_TYPES.EMAIL:
          IconComponent = MailIcon;
          break;
        case INSIGHT_TYPES.PHONE_NUMBER:
          IconComponent = PhoneIcon;
          break;
      }

      // Apply className once after determining the icon
      const icon = IconComponent ? (
        <IconComponent className="h-4 w-4 text-muted-foreground" />
      ) : null;

      return {
        id: insight.insight_id,
        label: insight.name,
        icon,
        onClick: () => onFilterClick?.(`insight-${insight.insight_id}`),
        searchText: `${insight.name} ${insight.description}`,
      };
    });
  }, [allInsights, onFilterClick]);

  const hasInsights = allInsights && allInsights.length > 0;

  // Create saved view menu items
  const savedViewMenuItems = useMemo((): SearchableMenuItem[] => {
    if (!savedViews || savedViews.length === 0 || !onLoadView) {
      return [];
    }

    return savedViews.map((view) => ({
      id: `view:${view.id}`,
      label: view.name,
      icon: null,
      onClick: () => onLoadView(view.id),
      searchText: view.name,
    }));
  }, [savedViews, onLoadView]);

  // Create channel submenu items based on available channels
  const channelMenuItems = useMemo((): SearchableMenuItem[] => {
    if (!availableChannels || availableChannels.length === 0) {
      return [];
    }

    return availableChannels.map((channel: IntegrationType) => {
      const localizedLabel = t(getChannelKey(channel));
      const Icon = getIntegrationByType(channel)?.Icon;

      // Build search text for this channel item
      // Include both the channel name and its localized label for searching
      const searchTokens: string[] = [channel];

      // Add localized label to search terms
      if (localizedLabel && typeof localizedLabel === 'string') {
        searchTokens.push(localizedLabel);
      }

      return {
        id: `channel:${channel}`,
        label: localizedLabel || channel,
        icon: Icon ? <Icon className="h-4 w-4 text-muted-foreground" /> : null,
        onClick: () => onFilterClick?.(`channel:${channel}`),
        searchText: searchTokens.join(' '),
        searchOnly: true,
      };
    });
  }, [availableChannels, t, onFilterClick]);

  const menuGroups: SearchableMenuGroup[] = useMemo(
    () => [
      // Saved filter views group (at the top)
      ...(savedViewMenuItems.length > 0
        ? [
            {
              id: 'saved-views-group',
              items: [
                {
                  id: 'saved-views',
                  label: t('brain_logs.saved_filter_views'),
                  icon: <BookmarkIcon className="h-4 w-4 text-muted-foreground" />,
                  children: savedViewMenuItems,
                },
              ],
            },
          ]
        : []),
      ...(hasInsights
        ? [
            {
              id: 'insights-group',
              items: [
                {
                  id: 'insights',
                  label: t('brain_logs.filters.insights'),
                  icon: <LightbulbIcon className="h-4 w-4 text-muted-foreground" />,
                  children: insightMenuItems,
                },
              ],
            },
          ]
        : []),
      {
        id: 'filters-group-1',
        separator: true,
        items: [
          {
            id: 'tags',
            label: t('brain_logs.filters.groups.tags'),
            icon: <FilterIcon type="tags" />,
            onClick: () => onFilterClick?.('tags'),
          },
          {
            id: 'environments',
            label: t('brain_logs.filters.groups.environments'),
            icon: <FilterIcon type="environments" />,
            onClick: () => onFilterClick?.('environments'),
          },
          {
            id: 'channels',
            label: t('brain_logs.filters.groups.channels'),
            icon: <FilterIcon type="channels" />,
            onClick: () => onFilterClick?.('channels'),
            children: channelMenuItems,
          },
          {
            id: 'agentIds',
            label: t('analytics.fields.agent'),
            icon: <FilterIcon type="agentIds" />,
            onClick: () => onFilterClick?.('agentIds'),
          },
          {
            id: 'collections-recalled',
            label: t('brain_logs.filters.groups.collections_recalled'),
            icon: <FilterIcon type="hasCollection" />,
            onClick: () => onFilterClick?.('hasCollection'),
          },
          {
            id: 'language-models',
            label: t('brain_logs.filters.groups.language_models'),
            icon: <FilterIconSpacer />,
            onClick: () => onFilterClick?.('language-models'),
          },
          {
            id: 'firstMessageType',
            label: t('analytics.fields.first_message_type'),
            icon: <MessageSquareIcon className="h-4 w-4 text-muted-foreground" />,
            onClick: () => onFilterClick?.('firstMessageType'),
          },
        ],
      },
      {
        id: 'metrics-group',
        separator: true,
        items: [
          {
            id: 'metrics',
            label: t('brain_logs.filters.groups.metrics'),
            icon: <ThumbsUpIcon className="h-4 w-4 text-muted-foreground" />,
            children: [
              {
                id: 'ratings',
                label: t('brain_logs.filters.metrics.ratings'),
                section: t('brain_logs.filters.metric_groups.user_review'),
                onClick: () => onFilterClick?.('ratings'),
              },
              {
                id: 'reactions',
                label: t('analytics.fields.reactions'),
                section: t('brain_logs.filters.metric_groups.user_review'),
                onClick: () => onFilterClick?.('reactions'),
              },
              {
                id: 'ai-csat-score',
                label: t('brain_logs.filters.metrics.ai_csat'),
                section: t('brain_logs.filters.metric_groups.ai_review'),
                onClick: () => onFilterClick?.('csatBrain'),
              },
              {
                id: 'ai-agent-goal',
                label: t('brain_logs.filters.metrics.ai_agent_goal'),
                section: t('brain_logs.filters.metric_groups.ai_review'),
                onClick: () => onFilterClick?.('goalAchieved'),
              },
              {
                id: 'user-sentiment',
                label: t('brain_logs.filters.metrics.user_sentiment'),
                section: t('brain_logs.filters.metric_groups.ai_review'),
                onClick: () => onFilterClick?.('userSentiment'),
              },
              {
                id: 'containment',
                label: t('brain_logs.filters.metrics.containment'),
                section: t('brain_logs.filters.metric_groups.conversation'),
                onClick: () => onFilterClick?.('isContained'),
              },
              {
                id: 'coverage',
                label: t('brain_logs.filters.metrics.coverage'),
                section: t('brain_logs.filters.metric_groups.conversation'),
                onClick: () => onFilterClick?.('isCovered'),
              },
              {
                id: 'meaningful',
                label: t('brain_logs.filters.metrics.meaningful'),
                section: t('brain_logs.filters.metric_groups.conversation'),
                onClick: () => onFilterClick?.('isMeaningful'),
              },
              {
                id: 'user-messages',
                label: t('brain_logs.filters.metrics.user_messages'),
                section: t('brain_logs.filters.metric_groups.conversation'),
                onClick: () => onFilterClick?.('minNumUserMessages'),
              },
            ],
          },
          {
            id: 'user-session-info',
            label: t('brain_logs.filters.groups.user_session_info'),
            icon: <UserRoundIcon className="h-4 w-4 text-muted-foreground" />,
            children: [
              {
                id: 'user-name',
                label: t('brain_logs.filters.user.user_name'),
                onClick: () => onFilterClick?.('userName'),
              },
              {
                id: 'user-id',
                label: t('brain_logs.filters.user.user_id'),
                onClick: () => onFilterClick?.('userId'),
              },
              {
                id: 'user-email',
                label: t('brain_logs.filters.user.user_email'),
                onClick: () => onFilterClick?.('userEmail'),
              },
              {
                id: 'external-user-id',
                label: t('brain_logs.filters.user.external_user_id'),
                onClick: () => onFilterClick?.('externalUserId'),
              },
              {
                id: 'channel-user-id',
                label: t('brain_logs.filters.user.channel_user_id'),
                onClick: () => onFilterClick?.('channelUserId'),
              },
              {
                id: 'conversation-id',
                label: t('brain_logs.filters.user.conversation_id'),
                onClick: () => onFilterClick?.('sessionId'),
              },
            ],
          },
        ],
      },
      {
        id: 'additional-group',
        separator: true,
        items: [
          {
            id: 'hasRecording',
            label: t('brain_logs.filters.groups.voice_recordings'),
            icon: <FilterIcon type="hasRecording" />,
            onClick: () => onFilterClick?.('hasRecording'),
          },
          {
            id: 'hasVoicemail',
            label: t('brain_logs.filters.groups.voicemail'),
            icon: <FilterIcon type="hasVoicemail" />,
            onClick: () => onFilterClick?.('hasVoicemail'),
          },
          {
            id: 'preview-conversations',
            label: t('brain_logs.filters.groups.preview_conversations'),
            icon: <FilterIcon type="isTest" />,
            onClick: () => onFilterClick?.('isTest'),
          },
        ],
      },
    ],
    [insightMenuItems, hasInsights, onFilterClick, channelMenuItems, savedViewMenuItems, t]
  );

  return { menuGroups };
};
